import React, { useMemo } from 'react';
import { useSelector } from 'react-redux';
import { Card, Stack, Group, Text, Badge, Button, Tooltip, Collapse, Paper, Progress, Divider } from '@mantine/core';
import { IconChevronDown, IconChevronRight, IconEdit, IconClipboardCheck, IconEye, IconAlertTriangle } from '@tabler/icons-react';
import RiskMitigationsInlineV2 from './components/RiskMitigationsInlineV2';

function toTitleCase(str = '') {
  return String(str)
    .replace(/([a-z])([A-Z])/g, '$1 $2')
    .replace(/([A-Z]+)([A-Z][a-z])/g, '$1 $2')
    .replace(/_/g, ' ')
    .replace(/\s+/g, ' ')
    .trim()
    .replace(/^\w|\s\w/g, (m) => m.toUpperCase());
}

const priorityColors = {
  Critical: 'red',
  High: 'orange', 
  Significant: 'yellow',
  Moderate: 'blue',
  Low: 'green',
  Negligible: 'gray',
  None: 'gray'
};

const priorityIcons = {
  Critical: '🔴',
  High: '⚠️',
  Significant: '🟡',
  Moderate: '🔵',
  Low: '🟢',
  Negligible: '⚪',
  None: '⚪'
};

export default function SectionAssessmentCardV2({
  modelId,
  assessmentUuid,
  sectionName,
  sectionData,
  originalSectionData,
  residualRiskData,
  onEvaluateAction,
  onEditCard,
  isExpanded = false,
  onToggleExpand,
  isMinimized = false
}) {
  const savedControls = useSelector((s) => s.controls.savedControls || {});
  const savedControlEvaluations = useSelector((s) => s.controls.savedControlEvaluations || {});

  // compute changed
  const isChanged = useMemo(() => {
    if (!originalSectionData || !sectionData) return false;
    try {
      const orig = JSON.stringify({ riskSources: originalSectionData.riskSources });
      const curr = JSON.stringify({ riskSources: sectionData.riskSources });
      return orig !== curr;
    } catch {
      return false;
    }
  }, [originalSectionData, sectionData]);

  // Enhanced stats calculation
  const sectionStats = useMemo(() => {
    const priorityCounts = {};
    let residualCount = 0;
    let totalActions = 0;
    let evaluatedActions = 0;
    let completedActions = 0;

    Object.values(sectionData?.sectionRisks || {}).forEach((risksArr) => {
      (Array.isArray(risksArr) ? risksArr : []).forEach((r) => {
        const pr = (r.priority || 'None').toString();
        priorityCounts[pr] = (priorityCounts[pr] || 0) + 1;
        if (residualRiskData[r.riskId]) residualCount += 1;

        // Count actions for this risk
        const actions = savedControls[r.riskId] || [];
        totalActions += actions.length;

        actions.forEach(action => {
          const evalData = savedControlEvaluations[r.riskId]?.[action.controlId]?.[0] || {};
          if (evalData.control_recurrence || evalData.executed_next || evalData.self_evaluation) {
            evaluatedActions += 1;
            if (evalData.control_recurrence === 'Complete' || evalData.executed_next === 'Complete') {
              completedActions += 1;
            }
          }
        });
      });
    });

    const topPriorities = Object.entries(priorityCounts)
      .sort((a, b) => {
        const priorityOrder = { Critical: 5, High: 4, Significant: 3, Moderate: 2, Low: 1, Negligible: 0, None: 0 };
        return (priorityOrder[b[0]] || 0) - (priorityOrder[a[0]] || 0);
      })
      .slice(0, 3);

    const totalRisks = Object.values(priorityCounts).reduce((sum, count) => sum + count, 0);
    const progressPercentage = totalActions > 0 ? Math.round((completedActions / totalActions) * 100) : 0;

    return {
      priorityCounts,
      topPriorities,
      residualCount,
      totalRisks,
      totalActions,
      evaluatedActions,
      completedActions,
      progressPercentage,
    };
  }, [sectionData, residualRiskData, savedControls, savedControlEvaluations]);

  // flattened risks list for expanded view
  const risksByCategory = useMemo(() => {
    const map = new Map();
    Object.entries(sectionData?.sectionRisks || {}).forEach(([category, arr]) => {
      if (Array.isArray(arr) && arr.length > 0) {
        map.set(category, arr);
      }
    });
    return map;
  }, [sectionData]);

  const handleExpand = () => {
    onToggleExpand(sectionName);
  };

  // Minimized view for when another card is expanded
  if (isMinimized) {
    return (
      <Card 
        withBorder 
        radius="md" 
        p="sm"
        style={{ cursor: 'pointer', transition: 'all 0.2s ease' }}
        onClick={handleExpand}
        bg="gray.0"
      >
        <Group justify="space-between" align="center">
          <Group gap="xs" align="center">
            <IconChevronRight size={16} />
            <Text fw={600} size="sm">{toTitleCase(sectionName)}</Text>
            {isChanged && <Badge color="yellow" variant="light" size="xs">Changed</Badge>}
          </Group>
          <Group gap="xs">
            <Badge variant="light" color="indigo" size="xs">
              {sectionStats.totalRisks} risks
            </Badge>
            <Badge variant="light" color="teal" size="xs">
              {sectionStats.progressPercentage}% done
            </Badge>
          </Group>
        </Group>
      </Card>
    );
  }

  const caret = isExpanded ? <IconChevronDown size={18} /> : <IconChevronRight size={18} />;

  return (
    <Card 
      withBorder 
      radius="md" 
      p={isExpanded ? "lg" : "md"}
      style={{ 
        transition: 'all 0.3s ease',
        minHeight: isExpanded ? '400px' : 'auto'
      }}
    >
      <Stack gap="md">
        {/* Header Section */}
        <Group justify="space-between" align="flex-start">
          <Group 
            gap="sm" 
            align="center" 
            onClick={handleExpand} 
            style={{ cursor: 'pointer', flex: 1 }}
          >
            {caret}
            <Stack gap={2}>
              <Group gap="xs" align="center">
                <Text fw={700} size={isExpanded ? "lg" : "md"}>
                  {toTitleCase(sectionName)}
                </Text>
                {isChanged && <Badge color="yellow" variant="light" size="sm">Modified</Badge>}
              </Group>
              <Text size="xs" c="dimmed">
                {sectionStats.totalRisks} risks identified • {sectionStats.totalActions} actions planned
              </Text>
            </Stack>
          </Group>

          {/* Priority Badges */}
          <Stack gap="xs" align="flex-end">
            {sectionStats.topPriorities.map(([priority, count]) => (
              <Badge 
                key={priority}
                variant="filled" 
                color={priorityColors[priority]} 
                size="sm"
                leftSection={priorityIcons[priority]}
              >
                {priority}: {count}
              </Badge>
            ))}
          </Stack>
        </Group>

        {/* Progress Section - Always Visible */}
        <Stack gap="xs">
          <Group justify="space-between" align="center">
            <Text size="sm" fw={500}>Mitigation Progress</Text>
            <Text size="sm" c="dimmed">{sectionStats.progressPercentage}%</Text>
          </Group>
          <Progress 
            value={sectionStats.progressPercentage} 
            color={sectionStats.progressPercentage > 75 ? 'green' : sectionStats.progressPercentage > 50 ? 'yellow' : 'red'}
            size="sm"
            radius="md"
          />
          <Group justify="space-between">
            <Text size="xs" c="dimmed">
              {sectionStats.completedActions} of {sectionStats.totalActions} actions complete
            </Text>
            <Text size="xs" c="dimmed">
              {sectionStats.evaluatedActions} evaluated
            </Text>
          </Group>
        </Stack>

        {/* Quick Actions - Collapsed View */}
        {!isExpanded && (
          <>
            <Divider />
            <Group justify="space-between" align="center">
              <Group gap="xs">
                <Badge variant="light" color="grape" size="sm">
                  Residuals: {sectionStats.residualCount}
                </Badge>
                <Tooltip label="View detailed breakdown">
                  <Button size="xs" variant="light" leftSection={<IconEye size={14} />} onClick={handleExpand}>
                    View Details
                  </Button>
                </Tooltip>
              </Group>
              <Group gap="xs">
                <Tooltip label="Edit risk sources and scenarios">
                  <Button 
                    size="xs" 
                    variant="light" 
                    leftSection={<IconEdit size={14} />}
                    onClick={() => onEditCard?.(sectionName)}
                  >
                    Edit Card
                  </Button>
                </Tooltip>
              </Group>
            </Group>
          </>
        )}

        {/* Expanded Content */}
        <Collapse in={isExpanded}>
          <Stack gap="lg">
            <Divider label="Risk Details" labelPosition="left" />
            
            {risksByCategory.size === 0 ? (
              <Card withBorder radius="sm" p="md" bg="gray.0">
                <Text c="dimmed" ta="center">No risks identified in this section yet.</Text>
              </Card>
            ) : (
              <Stack gap="md">
                {[...risksByCategory.entries()].map(([category, risks]) => (
                  <Card key={category} withBorder radius="sm" p="md" bg="blue.0">
                    <Stack gap="sm">
                      <Group justify="space-between" align="center">
                        <Text fw={600} size="md">{toTitleCase(category)}</Text>
                        <Badge variant="light" color="blue" size="sm">
                          {risks.length} {risks.length === 1 ? 'risk' : 'risks'}
                        </Badge>
                      </Group>

                      <Stack gap="sm">
                        {risks.map((risk) => (
                          <Paper key={risk.riskId} withBorder radius="sm" p="md" bg="white">
                            <Stack gap="sm">
                              {/* Risk Header */}
                              <Group justify="space-between" align="flex-start">
                                <Stack gap={4} style={{ flex: 1 }}>
                                  <Group gap="xs" align="center">
                                    <Text fw={600} size="sm" style={{ wordBreak: 'break-word' }}>
                                      {risk.riskName}
                                    </Text>
                                    {risk.priority && (
                                      <Badge 
                                        size="sm" 
                                        variant="filled" 
                                        color={priorityColors[risk.priority]}
                                        leftSection={priorityIcons[risk.priority]}
                                      >
                                        {risk.priority}
                                      </Badge>
                                    )}
                                  </Group>
                                  {risk.riskDescription && (
                                    <Text size="xs" c="dimmed" style={{ wordBreak: 'break-word' }}>
                                      {risk.riskDescription}
                                    </Text>
                                  )}
                                </Stack>
                                
                                {residualRiskData[risk.riskId] && (
                                  <Tooltip label="Has residual risk assessment">
                                    <Badge color="orange" variant="light" size="sm">
                                      <IconAlertTriangle size={12} />
                                    </Badge>
                                  </Tooltip>
                                )}
                              </Group>

                              {/* Risk Mitigations */}
                              <RiskMitigationsInlineV2
                                modelId={modelId}
                                assessmentUuid={assessmentUuid}
                                risk={risk}
                                onEvaluate={() => onEvaluateAction({ riskArea: sectionName, sourceName: risk.category })}
                              />
                            </Stack>
                          </Paper>
                        ))}
                      </Stack>
                    </Stack>
                  </Card>
                ))}
              </Stack>
            )}

            {/* Expanded Actions */}
            <Divider />
            <Group justify="flex-end" gap="md">
              <Button 
                size="sm" 
                variant="light" 
                leftSection={<IconEdit size={16} />}
                onClick={() => onEditCard?.(sectionName)}
              >
                Edit Sources & Scenarios
              </Button>
              <Button 
                size="sm" 
                variant="light" 
                leftSection={<IconClipboardCheck size={16} />}
                onClick={handleExpand}
              >
                Collapse View
              </Button>
            </Group>
          </Stack>
        </Collapse>
      </Stack>
    </Card>
  );
}